/**
 * League › FAAB — what each manager holds, and every line that got him there.
 *
 * Built from `handoff/design_handoff_faab_trades/FFL Pass 06 FAAB.dc.html`.
 *
 * THE ONE RULE: a balance is never stored. Everything on this screen is `openingBalance(gm)` plus
 * the entries, recomputed on every render — see `domain/faabLedger.ts`. Storing one is how "we
 * seeded everybody at ₣100" survived a correction to the rollover rule.
 *
 * TWO VIEWS OF ONE LEDGER. By GM answers "what have I got and why"; Full Ledger answers "does the
 * league add up". They are the same lines grouped differently, which is why the segmented control
 * is a view switch rather than two pages.
 *
 * WHERE THE LOG OPENS DEPENDS ON THE WIDTH, and that is a different TREE, not different CSS.
 * Beside a table on a desktop the panel is always in view and a default selection is a courtesy.
 * Stacked on a phone it landed below all ten rows — tapping Rajan scrolled nothing, and his log
 * was six hundred pixels further down with the whole standings table in between. So on a phone
 * the lines open UNDER the row that was tapped, and nothing is open until one is: an accordion
 * that pre-expands the leader is an accordion that opens on the wrong man.
 *
 * EDITING THE LEDGER, AND THE TRAIL THAT IS NOT IN IT (Tim, 9 September: "allow for admin/commish
 * to edit the FAAB ledger directly", then: "i understand the purpose of a ledger, but I need to
 * clean up entries in the faab ledger with perma-delete to remove the items I added manually
 * because it wasn't wired up correctly originally").
 *
 * The first version wrote a reversing line for every deletion, because `firestore.rules` made the
 * ledger append-only and the reason written above that rule is that a contested balance needs a
 * trail. Half right. It does — but the LEDGER did not have to be the trail, and making it one cost
 * two visible lines per correction: Tim cleaned up four bad rows and his ledger read as twelve,
 * every balance flanked by a struck-through twin.
 *
 * The trail lives in the audit log now, where `recordAudit` already keeps every other consequential
 * act. A deletion is recorded there with who did it, when, and the row's whole content — everything
 * a February argument needs and none of it in front of a man reading his balance in September. So
 * Delete deletes, and Edit is a delete and a fresh row rather than an in-place rewrite, because two
 * audit entries cannot be mistaken for the line having always said that.
 *
 * Rows written before this still carry `voids`, and the ledger still pairs and strikes them so the
 * existing data reads correctly — see `domain/faabLedger.ts`. Deleting a reversed row takes its
 * reversal with it, which is what "clean this up" has to mean.
 *
 * AND THE TRADES SQUARE THEMSELVES. FAAB has always been tradeable and the settlement had never
 * moved any (Tim: "i had FAAB in trades that should have been deducted/added as necessary once
 * completed. I made manual adjustments instead"). New trades post their own rows now; the strip
 * above the table is for the ones already settled, and it names what the archive says against what
 * the ledger holds rather than guessing which of his manual corrections covered which deal — see
 * `domain/tradeFaab.ts`.
 *
 * THE ENTRY BAR CHANGES SHAPE WITH THE KIND, and that is the design's sharpest idea. A purchase
 * takes dollars and derives the scrip at the fixed rate; a fine's amount comes from the rule and
 * is read-only; a correction cannot be saved without a reason, because it is the one kind with no
 * rule behind it. Four kinds, four different questions — one form with eight optional fields would
 * ask all of them at once and mean none of them.
 */

import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { FAAB, faab, faabFromRealMoney } from '../domain/faab.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import { chargeEntries, taxCharges } from '../domain/luxuryCharge.js';
import {
  KIND_LABEL, type LedgerFilter, type LedgerLine, type LedgerOrder, amountLabel, byDay, dayLabel,
  ledgerFor, matchesFilter, runningBalance, sortLines, sparkline, standings,
} from '../domain/faabLedger.js';
import { voidedIds } from '../domain/faabVoid.js';
import { auditEntry } from '../domain/auditEntry.js';
import { tradeFaabEntries, tradeFaabGaps, tradeFaabSummary } from '../domain/tradeFaab.js';
import { FACE_FILL, gmColor, initials } from '../domain/gmColors.js';
import { useAvatar } from './avatars.js';
import { useMinWidth } from './useMinWidth.js';
import { Rule } from './RuleText.js';
import { GMS } from '../store/session.js';
import {
  atLeast, type FaabEntry, type GmId, type KeeperDeclaration, type LeagueStore, type Role,
  type Trade,
} from '../store/types.js';

const SEASON = 2026;
/* The sparkline's vertical scale, shared by every manager — see `sparkline`. */
const SPARK_MAX = 150;

type View = 'gm' | 'ledger';
type EntryKind = 'purchase' | 'waivers' | 'fine' | 'keeper' | 'trade' | 'correction';

/** The fines the rulebook sets, so the amount is never typed. */
const OFFENCES: ReadonlyArray<{ id: string; what: string; faab: number; rule: string }> = [
  { id: 'drop', what: 'Drafted then dropped before Week 1', faab: 5, rule: '2019-III-4' },
  { id: 'rename', what: 'Refusing to accept a renaming', faab: 50, rule: '2020-I-5' },
];

export function Faab({ gm, role, store }: { gm: string; role: Role; store: LeagueStore }) {
  const [entries, setEntries] = useState<FaabEntry[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [view, setView] = useState<View>('gm');
  const [sel, setSel] = useState<string | null>(null);
  const [filter, setFilter] = useState<LedgerFilter>('all');
  /*
   * NEWEST FIRST BY DEFAULT, in both views. A ledger accumulates oldest-first and is READ
   * newest-first for most of a season: what moved today is the question, and the openings that
   * never change were taking the top twenty rows of a thirty-four row ledger.
   */
  const [order, setOrder] = useState<LedgerOrder>('new');
  /* The line whose Edit is open. One at a time: two open forms on one ledger is two answers. */
  const [editing, setEditing] = useState<string | null>(null);
  const commish = atLeast(role, 'commissioner');

  useEffect(() => {
    let live = true;
    const load = () => store.faab(SEASON).then((x) => { if (live) setEntries(x); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'faab') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  /*
   * The archive, read only for a commissioner: it is what the trade strip compares the ledger
   * against, and a GM who cannot post a row has no use for the comparison.
   */
  useEffect(() => {
    if (!commish) return undefined;
    let live = true;
    const load = () => store.trades(SEASON).then((x) => { if (live) setTrades(x); }).catch(() => {});
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'trades') void load(); });
    return () => { live = false; off(); };
  }, [store, commish]);

  const table = useMemo(() => standings(entries, GMS), [entries]);
  const lines = useMemo(() => ledgerFor(entries, GMS), [entries]);

  /**
   * The commissioner's two acts on a stored line, and the audit entry each one leaves.
   *
   * A ROW NEVER GOES WITHOUT A NOTE. The note is the whole justification for letting the ledger be
   * cleaned at all, so it carries the row's content — GM, amount, kind, description, date — and not
   * just its id: an audit line saying "deleted faab-x1" answers nothing in February.
   *
   * A REVERSED ROW GOES WITH ITS REVERSAL. Those pairs are the previous design's residue, and
   * leaving half of one behind would turn a balance that netted to nothing into a live credit.
   */
  const act = useMemo(() => {
    const said = (e: FaabEntry) => `${managerShort(e.gm)} ${amountLabel(e.amount)} `
      + `(${e.kind}) on ${e.date} — ${e.description}`;
    const note = (what: string, e: FaabEntry) => {
      const n = auditEntry(SEASON, gm.toUpperCase(), {
        kind: 'delete', onBehalfOf: e.gm, target: `seasons/${SEASON}/faab/${e.id}`, what,
      });
      void store.recordAudit?.(n).catch(() => {});
    };
    /** The row, plus any reversal of it left over from the append-only design. */
    const withPair = (id: string) => {
      const row = entries.find((e) => e.id === id);
      if (!row) return [];
      return [row, ...entries.filter((e) => e.voids === id)];
    };
    const drop = async (id: string, why: string) => {
      for (const e of withPair(id)) {
        note(`Removed a FAAB row: ${said(e)}${why ? ` — ${why}` : ''}`, e);
        await store.deleteFaab(SEASON, e.id);
      }
    };
    return {
      remove: drop,
      amend: async (id: string, amount: number, description: string) => {
        const row = entries.find((e) => e.id === id);
        if (!row) return;
        /*
         * The replacement first, and under a NEW id, so a failure between the two writes leaves the
         * ledger over-stating a man rather than losing his money — an extra line somebody can see
         * and delete, rather than a balance quietly short.
         */
        const next: FaabEntry = {
          ...row,
          id: `faab-${Date.now().toString(36)}`,
          amount,
          description: description.trim() || row.description,
          recordedBy: gm.toUpperCase() as GmId,
        };
        await store.saveFaab(next);
        note(`Corrected a FAAB row to ${amountLabel(amount)} — ${next.description}`, row);
        await drop(id, '');
      },
    };
  }, [entries, store, gm]);

  /** Nothing but an opening line is beyond a commissioner's reach now. */
  const cancelled = useMemo(() => voidedIds(entries), [entries]);
  const tools = commish
    ? {
      editing,
      onEdit: (id: string | null) => setEditing(id),
      /* A reversal is not deleted on its own: it goes with the row it cancels, or the pair breaks. */
      canRemove: (id: string) => {
        const row = entries.find((e) => e.id === id);
        return Boolean(row) && !row!.voids;
      },
      voided: (id: string) => cancelled.has(id),
      remove: act.remove,
      amend: act.amend,
    }
    : undefined;
  const wide = useMinWidth(900);
  const panelId = useId();
  /* On the wide layout, never empty: re-clicking the selected row clears it, and the side panel
     falls back to the leader rather than showing a card with nothing in it. On a phone there is
     no card to leave empty, so an unselected table simply has nothing open. */
  const shown = table.find((s) => s.gm === sel) ?? (wide ? table[0] : undefined);

  return (
    <div className={`app fbpage${commish ? ' tools' : ''}`}>
      <header className="fbhead">
        <div className="fbheadtext">
          <div className="fbtitle">
            <h1>FAAB</h1>
            <Rule id="2018-II-4"><span aria-label="FAAB rules">§</span></Rule>
          </div>
          {/* The Full Ledger explains itself once you are looking at it — the group headers, the
              net per day and the footnote all say the same thing the sub-line did. */}
          {view === 'gm' && (
            <p className="fbsub">
              {wide ? 'Click a GM to see their FAAB log.' : 'Tap a GM to open their FAAB log.'}
            </p>
          )}
        </div>
        <div className="fbseg" role="tablist" aria-label="FAAB views">
          <button type="button" role="tab" aria-selected={view === 'gm'}
            className={`fbsegitem ${view === 'gm' ? 'on' : ''}`} onClick={() => setView('gm')}>
            By GM
          </button>
          <button type="button" role="tab" aria-selected={view === 'ledger'}
            className={`fbsegitem ${view === 'ledger' ? 'on' : ''}`}
            onClick={() => setView('ledger')}>
            Full Ledger
          </button>
        </div>
      </header>

      {commish && <EntryBar gm={gm} store={store} ledger={entries} />}
      {commish && <TradeStrip gm={gm} store={store} ledger={entries} trades={trades} />}

      {view === 'gm' ? (
        <div className="fbgrid">
          <section className="fbtable">
            <div className="fbthead">
              <span>GM</span>
              <span>2026 opening &rarr; now</span>
              <span className="num">Opening</span>
              <span className="num">Bought</span>
              <span className="num">Balance</span>
            </div>
            {table.map((s) => {
              const open = !wide && sel === s.gm;
              return (
                <Fragment key={s.gm}>
                  <button type="button"
                    className={`fbtrow ${shown?.gm === s.gm ? 'on' : ''} ${open ? 'open' : ''}`}
                    {...(wide ? {} : {
                      'aria-expanded': open, 'aria-controls': `${panelId}-${s.gm}`,
                    })}
                    onClick={() => setSel((v) => (v === s.gm ? null : s.gm))}>
                    <span className="fbwho">
                      <Face gm={s.gm} size={28} />
                      <b>{managerShort(s.gm)}</b>
                    </span>
                    <span className="fbspark">
                      <svg viewBox="0 0 240 30" aria-hidden="true">
                        <path
                          d={sparkline(runningBalance(s), { width: 238, height: 26, max: SPARK_MAX })}
                          fill="none" stroke={gmColor(s.gm)} strokeWidth="1.8"
                          strokeLinejoin="round" strokeLinecap="round" strokeOpacity=".85" />
                      </svg>
                    </span>
                    <span className="num fbopen">{faab(s.opening)}</span>
                    <span className={`num fbbought ${s.bought ? 'yes' : ''}`}>
                      {s.bought ? `+${faab(s.bought)}` : '—'}
                    </span>
                    <span className="num fbbal">{faab(s.balance)}</span>
                  </button>
                  {/*
                    NO HEADER ON THE INLINE PANEL. The row directly above it already carries the
                    face, the name and the balance; repeating them is the same three facts twice
                    in forty pixels. And no inner scroller — a scrolling box inside a scrolling
                    page is the phone gesture nobody wins.
                  */}
                  {open && (
                    <div className="fbinline" id={`${panelId}-${s.gm}`}>
                      {sortLines(s.lines, order).map((l, i) => (
                        <Line key={l.id ?? `${l.date}-${i}`} line={l} tools={tools} />
                      ))}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </section>

          {wide && shown && (
            <section className="fbdetail">
              <header className="fbdethead">
                <span className="fbdetwho">
                  <Face gm={shown.gm} size={32} />
                  <b>{managerShort(shown.gm)}</b>
                </span>
                <span className="fbdethdr">
                  <SortToggle order={order} onOrder={setOrder} />
                  <b className="fbdetbal">{faab(shown.balance)}</b>
                </span>
              </header>
              <div className="fbdetscroll">
                {sortLines(shown.lines, order).map((l, i) => (
                  <Line key={l.id ?? `${l.date}-${i}`} line={l} tools={tools} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <FullLedger lines={lines} filter={filter} onFilter={setFilter}
          order={order} onOrder={setOrder} tools={tools} />
      )}
    </div>
  );
}

/**
 * The ringed circle. Grey underneath, identity in the ring — see `domain/gmColors.ts`.
 *
 * A manager who has uploaded a picture gets the picture INSIDE that ring, not beside it: the
 * circle is a fixed-size cell in a standings row and a ledger line, and making room for both
 * would shift every column that has one. The ring is left alone, so a photographed manager and
 * an initialled one are still recognisably the same league.
 *
 * The background is conditional rather than constant because the inline style would otherwise
 * beat `.fbface.hasface` and paint pale grey over the photo's transparent edges.
 */
function Face({ gm, size }: { gm: string; size: number }) {
  const face = useAvatar(gm);
  return (
    <span className={`fbface${face ? ' hasface' : ''}`} aria-hidden="true"
      style={{
        width: size, height: size, borderColor: gmColor(gm),
        background: face ? undefined : FACE_FILL,
        fontSize: size <= 26 ? 10 : size <= 30 ? 10.5 : size <= 32 ? 11 : 12,
      }}>
      {face ? <img src={face} alt="" draggable={false} /> : initials(managerShort(gm))}
    </span>
  );
}

/**
 * What a commissioner may do to a line, passed down rather than reached for.
 *
 * One object rather than five props because it travels through three different renderers — the
 * inline phone panel, the desktop detail panel and the full ledger — and every one of them would
 * otherwise carry five props it does nothing with but forward. Absent for everybody but a
 * commissioner, which is also how the markup stays identical for a GM: no disabled buttons, no
 * `Commissioner` chrome greyed out beside his own balance.
 */
export interface LineTools {
  editing: string | null;
  onEdit: (id: string | null) => void;
  canRemove: (id: string) => boolean;
  /** Some older row reverses this one, so removing it removes the pair. */
  voided: (id: string) => boolean;
  remove: (id: string, why: string) => Promise<void>;
  amend: (id: string, amount: number, description: string) => Promise<void>;
}

function Line({ line, tools }: { line: LedgerLine; tools?: LineTools }) {
  const open = Boolean(tools && line.id && tools.editing === line.id);
  const editable = Boolean(tools && line.id && line.stored && tools.canRemove(line.id));
  return (
    <div className={`fbline${line.voided ? ' struck' : ''}${open ? ' editing' : ''}`}>
      <span className="fblinetext">
        <span className="fblinetop">
          <time className="fbdate">{line.date}</time>
          <span className={`fbkind ${line.kind}`}>{KIND_LABEL[line.kind]}</span>
          {line.voided && <span className="fbkind void">Reversed</span>}
        </span>
        <span className="fbwhat">
          {line.description}
          {line.rule && <Rule id={line.rule}><span aria-label={`Rule ${line.rule}`}>§</span></Rule>}
        </span>
        {open && line.id && (
          <EditLine line={line} id={line.id} tools={tools!} />
        )}
      </span>
      <b className={`fbamt ${line.amount < 0 ? 'neg' : line.amount > 0 ? 'pos' : 'nil'}`}>
        {amountLabel(line.amount)}
      </b>
      {editable && line.id && (
        <LineActs id={line.id} open={open} tools={tools!} pair={tools!.voided(line.id)} />
      )}
    </div>
  );
}

/**
 * Edit and Delete, and the reason Delete asks twice.
 *
 * The row is GONE when this is pressed — not struck through, not reversed, off the ledger and out
 * of the balance, with the audit log holding what it said. So the button asks, in place, rather
 * than opening a dialogue: the confirmation is the second press of the same control, which is the
 * smallest thing that can stand between a mis-click and a balance the league argues about.
 */
function LineActs(
  { id, open, tools, pair }: { id: string; open: boolean; tools: LineTools; pair: boolean },
) {
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!arm) return undefined;
    const t = setTimeout(() => setArm(false), 4000);
    return () => clearTimeout(t);
  }, [arm]);
  return (
    <span className="fbacts">
      <button type="button" className={`fbact ${open ? 'on' : ''}`}
        title="Change the amount or the wording — replaces this row and notes it in the audit log"
        onClick={() => tools.onEdit(open ? null : id)}>
        {open ? 'Cancel' : 'Edit'}
      </button>
      <button type="button" className={`fbact del ${arm ? 'arm' : ''}`} disabled={busy}
        title={arm
          ? `Press again to delete${pair ? ' this row and its reversal' : ''} for good`
          : pair
            ? 'Delete this row and the reversal under it — the audit log keeps both'
            : 'Delete this row for good — the audit log keeps what it said'}
        onClick={() => {
          if (!arm) { setArm(true); return; }
          setBusy(true);
          void tools.remove(id, '').finally(() => { setBusy(false); setArm(false); });
        }}>
        {arm ? 'Confirm' : 'Delete'}
      </button>
    </span>
  );
}

/**
 * The amount and the wording, and nothing else.
 *
 * Not the GM and not the date. Moving a line to another manager is two balances changing at once and
 * reads as one edit, which is how money goes missing; and the date is what the ledger is sorted and
 * grouped by, so a corrected date would move the line out from under the reader's eye. Both are
 * doable the honest way — Delete this line, then record the right one.
 */
function EditLine({ line, id, tools }: { line: LedgerLine; id: string; tools: LineTools }) {
  const [amount, setAmount] = useState(String(Math.abs(line.amount)));
  const [sign, setSign] = useState<'+' | '−'>(line.amount < 0 ? '−' : '+');
  const [what, setWhat] = useState(line.description);
  const [busy, setBusy] = useState(false);
  const next = (sign === '−' ? -1 : 1) * (Number(amount) || 0);
  const changed = next !== line.amount || what.trim() !== line.description;
  return (
    <span className="fbedit">
      <span className="fbeditrow">
        <button type="button" className="fbsign" aria-label="Credit or spend"
          onClick={() => setSign((v) => (v === '+' ? '−' : '+'))}>{sign}</button>
        <label className="fbfield num"><span className="pre">{FAAB}</span>
          <input value={amount} inputMode="numeric" aria-label="Amount"
            onChange={(e) => setAmount(e.target.value)} /></label>
        <label className="fbfield grow">
          <input value={what} aria-label="What it was for"
            onChange={(e) => setWhat(e.target.value)} /></label>
        <button type="button" className="fbgo" disabled={!changed || busy}
          onClick={() => {
            setBusy(true);
            void tools.amend(id, next, what).finally(() => { setBusy(false); tools.onEdit(null); });
          }}>Save</button>
      </span>
      <p className="fbhelp">
        Replaces {amountLabel(line.amount)} with {amountLabel(next)}. The old row comes off the
        ledger and the audit log keeps what it said.
      </p>
    </span>
  );
}

/**
 * FAAB the archive says moved and the ledger never saw.
 *
 * ONE STRIP, NOT TEN ROWS. Every trade with scrip in it produces two entries that are equal and
 * opposite, so listing them per GM would show the same deal twice and make a reader add it back
 * together. The strip lists DEALS — who paid whom, how much, and whether the ledger has it.
 *
 * IT WILL NOT GUESS ABOUT HIS MANUAL CORRECTIONS. The rows Tim wrote by hand are adjustments, and
 * an adjustment cannot be told apart from any other adjustment; posting these on the assumption
 * that they were something else would double a man's money. So a deal he already squared by hand
 * still shows here as unposted, and the honest fix is to reverse the manual line — which is now one
 * press away on the line itself — and then post. The help text says exactly that, because a
 * commissioner who does not know it will press the button and be wrong.
 */
function TradeStrip(
  { gm, store, ledger, trades }:
  { gm: string; store: LeagueStore; ledger: readonly FaabEntry[]; trades: readonly Trade[] },
) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const gaps = useMemo(() => tradeFaabGaps(trades, ledger, SEASON), [trades, ledger]);
  /*
   * ONE ITEM PER DEAL, AND THE DIRECTION COMES FROM `says`, NOT FROM `delta`.
   *
   * A fresh unposted deal produces two gaps whose deltas are equal and opposite, so the negative one
   * looked like the payer and listing those read correctly — until a posted row was edited, when the
   * gap became a two-pound correction and the list said "John pays Luke ₣10" about the man who had
   * received it. `says` is what the archive says the deal did and does not move when the ledger
   * does; `delta` is only how far behind the ledger is. And grouping by trade is what makes a
   * one-sided gap still name its deal.
   */
  const deals = useMemo(() => {
    const by = new Map<string, typeof gaps>();
    for (const g of gaps) by.set(g.tradeId, [...(by.get(g.tradeId) ?? []), g]);
    return [...by.values()].map((rows_) => {
      const g = rows_.find((x) => x.says < 0) ?? rows_[0]!;
      const sent = g.says < 0;
      return {
        tradeId: g.tradeId,
        date: g.date,
        payer: sent ? g.gm : g.other,
        payee: sent ? g.other : g.gm,
        amount: Math.abs(g.says),
        /* One figure for "of it": the sending side's if that side is short, else the receiving
           side's — a deal can be one-sided in the ledger, and quoting a nil from the side that
           happens to be right would say the money is missing when half of it is there. */
        posted: Math.abs((rows_.find((x) => x.says < 0) ?? rows_[0]!).posted),
        short: rows_.reduce((n, x) => n + Math.abs(x.delta), 0),
      };
    });
  }, [gaps]);
  const { rows } = tradeFaabSummary(gaps);
  if (rows === 0) return null;
  const post = () => {
    setBusy(true);
    void (async () => {
      const write = tradeFaabEntries(gaps, SEASON, gm.toUpperCase() as GmId, new Date().toISOString());
      for (const e of write) await store.saveFaab(e);
    })().finally(() => setBusy(false));
  };
  return (
    <section className="fbtrade">
      <div className="fbtraderow">
        <span className="fbbarlabel">
          <span className="fbtag amber">Commissioner</span>
          <b>{deals.length === 1 ? 'A trade moved FAAB' : `${deals.length} trades moved FAAB`}</b>
        </span>
        <span className="fbtradesay">
          the ledger has {rows === 1 ? 'a line' : `${rows} lines`} missing
        </span>
        <button type="button" className="fbact" aria-expanded={open}
          onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Which'}</button>
        <button type="button" className="fbgo" disabled={busy} onClick={post}>
          Post {rows} {rows === 1 ? 'line' : 'lines'}
        </button>
      </div>
      {open && (
        <ul className="fbtradelist">
          {deals.map((d) => (
            <li key={d.tradeId}>
              <time className="fbdate">{d.date.slice(0, 10)}</time>
              <b>{managerShort(d.payer)}</b> pays <b>{managerShort(d.payee)}</b> {faab(d.amount)}
              <span className="fbwhat">
                {d.posted === 0
                  ? ' — nothing in the ledger'
                  : ` — the ledger has ${faab(d.posted)} of it`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="fbhelp">
        FAAB is an asset and settling a trade moves it. These deals settled before it did. If you
        already squared one of them with a correction by hand, reverse that line first — this cannot
        tell a hand-made correction from any other adjustment, and posting on top of one would pay
        the man twice.
      </p>
    </section>
  );
}

const FILTERS: ReadonlyArray<{ id: LedgerFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'openings', label: 'Openings' },
  { id: 'purchase', label: 'Purchases' },
  /* Its own chip because a trade has a counterparty, which no other kind of line does. */
  { id: 'trade', label: 'Trades' },
  /* Its own chip because it is assessed from a slate, not decided by anybody. */
  { id: 'luxury', label: 'Luxury' },
  { id: 'farm', label: 'Farm' },
  { id: 'adjustment', label: 'Adjustments' },
];

/**
 * Newest first or oldest first, in both views at once.
 *
 * ONE CONTROL, ONE PIECE OF STATE. The two views are the same lines grouped differently, and a man
 * who sets the GM log to newest and then switches to the full ledger has not changed his mind about
 * which end he reads from.
 */
function SortToggle({ order, onOrder }: { order: LedgerOrder; onOrder: (o: LedgerOrder) => void }) {
  return (
    <span className="fbsort" role="group" aria-label="Order">
      <button type="button" className={`fbchip ${order === 'new' ? 'on' : ''}`}
        aria-pressed={order === 'new'} title="Most recent first"
        onClick={() => onOrder('new')}>Newest</button>
      <button type="button" className={`fbchip ${order === 'old' ? 'on' : ''}`}
        aria-pressed={order === 'old'} title="Oldest first, the way a balance accumulates"
        onClick={() => onOrder('old')}>Oldest</button>
    </span>
  );
}

function FullLedger(
  { lines, filter, onFilter, order, onOrder, tools }:
  {
    lines: LedgerLine[]; filter: LedgerFilter; onFilter: (f: LedgerFilter) => void;
    order: LedgerOrder; onOrder: (o: LedgerOrder) => void;
    tools?: LineTools;
  },
) {
  /* The whole list goes in with each line, so a reversal filters with what it reverses. */
  const shown = lines.filter((l) => matchesFilter(l, filter, lines));
  /* The days flip with the lines, or newest-first would put today's group at the bottom. */
  const groups = order === 'new' ? [...byDay(shown)].reverse() : byDay(shown);
  return (
    <section className="fbledger">
      <header className="fbledgerhd">
        <span className="fbcount">Ledger · {shown.length} of {lines.length} entries</span>
        <span className="fbchips">
          <SortToggle order={order} onOrder={onOrder} />
          <span className="fbchipgap" aria-hidden="true" />
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`fbchip ${filter === f.id ? 'on' : ''}`}
              aria-pressed={filter === f.id} onClick={() => onFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </span>
      </header>
      {groups.map((g) => (
        <div key={g.date} className="fbgroup">
          <div className="fbgrouphd">
            <span className="fbgroupday">{dayLabel(g.date)}</span>
            <span className="fbgroupnet">
              {g.lines.length} {g.lines.length === 1 ? 'entry' : 'entries'} · {amountLabel(g.net)}
            </span>
          </div>
          {sortLines(g.lines, order).map((l, i) => {
            const open = Boolean(tools && l.id && tools.editing === l.id);
            const editable = Boolean(tools && l.id && l.stored && tools.canRemove(l.id));
            return (
              <div key={l.id ?? `${l.gm}-${i}`}
                className={`fblrow${l.voided ? ' struck' : ''}${open ? ' editing' : ''}`}>
                <span className="fbwho">
                  <Face gm={l.gm} size={26} />
                  <b>{managerShort(l.gm)}</b>
                </span>
                <span className={`fbkind flat ${l.kind}`}>{KIND_LABEL[l.kind]}</span>
                <span className="fbwhat">
                  {l.description}
                  {open && l.id && <EditLine line={l} id={l.id} tools={tools!} />}
                </span>
                <b className={`fbamt ${l.amount < 0 ? 'neg' : l.amount > 0 ? 'pos' : 'nil'}`}>
                  {amountLabel(l.amount)}
                </b>
                {editable && l.id && <LineActs id={l.id} open={open} tools={tools!} pair={tools!.voided(l.id)} />}
              </div>
            );
          })}
        </div>
      ))}
      {groups.length === 0 && <p className="note">Nothing matches that filter.</p>}
      <p className="fbfoot">
        Openings are derived from the rollover rule and the 2025 ending balances, so they carry the
        same date and cannot be edited line by line.
      </p>
    </section>
  );
}

/**
 * The commissioner's entry bar — four kinds, four shapes.
 *
 * WHAT EACH KIND REFUSES TO LET YOU DO is the point of splitting them:
 *   purchase — you type dollars; the scrip is DERIVED at the fixed rate and cannot be edited, so
 *              the $2:₣1 rule cannot be broken by a typo.
 *   fine     — the amount comes from the offence, read-only, because 2019-III-4 and 2020-I-5 set
 *              it and a commissioner choosing the number is how a rule quietly becomes a habit.
 *   waivers  — the winning bid only. Yahoo runs the claim; this is a catch-up entry.
 *   keeper   — the luxury tax on one man's slate (Tim, 3 September: "need a keeper penalty on the
 *              faab ledger page"). The amount is assessed from his declaration, never typed, and
 *              the row is the same row League Overview writes when it charges all ten at once, via
 *              `luxuryCharge.ts` — same id sequence, same delta arithmetic — so the two doors
 *              cannot disagree and a man charged from either is charged once.
 *   correction — the one kind with no rule behind it, so it will not save without a reason.
 */
function EntryBar(
  { gm, store, ledger }: { gm: string; store: LeagueStore; ledger: readonly FaabEntry[] },
) {
  const [kind, setKind] = useState<EntryKind>('purchase');
  /*
   * Declarations are read only once the keeper kind is chosen. The ledger page does not otherwise
   * need ten slates to draw, and the read is the size of the league's keeper data.
   */
  const [decls, setDecls] = useState<KeeperDeclaration[] | null>(null);
  useEffect(() => {
    if (kind !== 'keeper' || decls !== null) return undefined;
    let live = true;
    void store.declarations(SEASON).then((d) => { if (live) setDecls(d); }).catch(() => {});
    return () => { live = false; };
  }, [kind, decls, store]);
  const [who, setWho] = useState('');
  const [dollars, setDollars] = useState('50');
  const [note, setNote] = useState('');
  const [player, setPlayer] = useState('');
  const [bid, setBid] = useState('22');
  const [week, setWeek] = useState('3');
  const [offence, setOffence] = useState(OFFENCES[0]!.id);
  const [sign, setSign] = useState<'+' | '−'>('+');
  /* The other man in a hand-entered trade. `who` is the one paying. */
  const [toGm, setToGm] = useState('');
  const [scrip, setScrip] = useState('10');
  const [amount, setAmount] = useState('1');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const bought = faabFromRealMoney(Number(dollars) || 0, LEAGUE_2026);
  const fine = OFFENCES.find((o) => o.id === offence) ?? OFFENCES[0]!;
  /*
   * WHAT HIS SLATE OWES, AND WHAT THE LEDGER HAS TAKEN. One row or none: `taxCharges` returns a
   * row only where the two disagree, so a man already charged the right amount produces nothing
   * and the button says so instead of charging him twice.
   */
  const keeper = useMemo(() => {
    if (kind !== 'keeper' || who === '' || decls === null) return null;
    const d = decls.find((x) => x.gm.toUpperCase() === who.toUpperCase());
    const tax = d
      ? assessLuxuryTax({
        teamId: who,
        declared: d.keepers.map((k) => ({
          playerId: k.player,
          franchiseTagged: k.franchiseTagged,
          ...(k.farmForfeited ? { farmForfeited: true } : {}),
        })),
      }, LEAGUE_2026).taxFaab
      : 0;
    const submitted = d?.status === 'submitted';
    const [row] = taxCharges([{ gm: who as GmId, tax, submitted }], ledger, SEASON);
    return { submitted, tax, row: row ?? null };
  }, [kind, who, decls, ledger]);

  const ready = who !== ''
    && (kind !== 'correction' || reason.trim() !== '')
    && (kind !== 'trade' || (toGm !== '' && toGm !== who && (Number(scrip) || 0) > 0))
    && (kind !== 'keeper' || Boolean(keeper?.row));

  async function save() {
    if (!ready) return;
    setBusy(true);
    const now = new Date();
    const base = {
      id: `faab-${now.getTime().toString(36)}`,
      season: SEASON, gm: who, date: now.toISOString().slice(0, 10), recordedBy: gm.toUpperCase(),
    };
    if (kind === 'keeper') {
      /* Through the same code as League Overview, so the id sequence and the arithmetic match. */
      if (!keeper?.row) { setBusy(false); return; }
      const [e] = chargeEntries([keeper.row], SEASON, gm.toUpperCase() as GmId, now.toISOString());
      await store.saveFaab(e!);
      setBusy(false);
      return;
    }
    /*
     * A TRADE IS TWO ROWS, WRITTEN TOGETHER (Tim, 9 September: "add manual entry categories for
     * Trade"). Settling a trade in the app posts both sides itself — see `domain/tradeFaab.ts` —
     * and this is for the deals that never went through the form: a handshake before the draft, or
     * one of this season's that settled before the money moved at all. One side would leave the
     * league's total wrong by the amount, which is the one error a ledger cannot absorb.
     */
    if (kind === 'trade') {
      const amt = Math.abs(Number(scrip) || 0);
      const stamp = now.getTime().toString(36);
      const rows: FaabEntry[] = [
        { ...base, id: `trade-manual-${stamp}-${who}`, gm: who, amount: -amt, kind: 'trade',
          description: `FAAB sent to ${managerShort(toGm)} in trade`
            + (note.trim() ? ` — ${note.trim()}` : '') },
        { ...base, id: `trade-manual-${stamp}-${toGm}`, gm: toGm, amount: amt, kind: 'trade',
          description: `FAAB from ${managerShort(who)} in trade`
            + (note.trim() ? ` — ${note.trim()}` : '') },
      ];
      for (const r of rows) await store.saveFaab(r);
      setBusy(false);
      setNote('');
      return;
    }
    const row: FaabEntry =
      kind === 'purchase'
        ? { ...base, amount: bought, kind: 'purchase',
          description: `Bought ${faab(bought)} for $${Number(dollars) || 0}`
            + (note.trim() ? ` — ${note.trim()}` : '') }
        : kind === 'waivers'
          ? { ...base, amount: -(Number(bid) || 0), kind: 'waiver',
            description: `Waiver claim, week ${week}${player.trim() ? `: ${player.trim()}` : ''}` }
          : kind === 'fine'
            ? { ...base, amount: -fine.faab, kind: 'fine',
              description: `${fine.what}${note.trim() ? ` — ${note.trim()}` : ''}` }
            : { ...base, amount: (sign === '−' ? -1 : 1) * (Number(amount) || 0),
              kind: 'adjustment', description: reason.trim() };
    await store.saveFaab(row);
    setBusy(false);
    setNote(''); setPlayer(''); setReason('');
  }

  return (
    <section className="fbbar">
      <div className="fbbarrow">
        <span className="fbbarlabel">
          <span className="fbtag">Commissioner</span>
          <b>Record FAAB Transaction</b>
        </span>
        <label className="fbpick">
          <span className="vh">Which GM</span>
          <select value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Which GM</option>
            {GMS.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
          </select>
        </label>
        <div className="fbkinds" role="tablist" aria-label="What kind of transaction">
          {(['purchase', 'waivers', 'fine', 'keeper', 'trade', 'correction'] as EntryKind[]).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k}
              className={`fbkindbtn ${kind === k ? 'on' : ''}`} onClick={() => setKind(k)}>
              {/*
                THE LEAGUE'S OWN WORDS (Tim, 8 September: "label the FAAB filters: waiver claim ->
                waivers, keeper penalty -> luxury"). `Waiver claim` was Yahoo's phrase for a thing
                the league calls waivers, and `Keeper penalty` named the charge after the thing it
                punishes rather than after what it is — the ledger row, the League Overview charge
                and `luxuryCharge.ts` have all said luxury tax since it was written.
              */}
              {k === 'waivers' ? 'Waivers' : k === 'purchase' ? 'Purchase'
                : k === 'fine' ? 'Fine' : k === 'keeper' ? 'Luxury'
                  : k === 'trade' ? 'Trade' : 'Correction'}
            </button>
          ))}
        </div>
      </div>

      <div className="fbbarbody">
        {kind === 'purchase' && (
          <>
            <div className="fbfields">
              <label className="fbfield num"><span className="pre">$</span>
                <input value={dollars} inputMode="numeric"
                  onChange={(e) => setDollars(e.target.value)} /></label>
              <span className="fbjoin">buys</span>
              {/* DERIVED, and read-only for it: the rate is the rule. */}
              <span className="fbfield num derived" aria-live="polite">
                <span className="pre">{FAAB}</span><b>{bought}</b>
              </span>
              <label className="fbfield grow">
                <input value={note} placeholder="Where it was declared — optional"
                  onChange={(e) => setNote(e.target.value)} /></label>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>Credit {faab(bought)}</button>
            </div>
            <p className="fbhelp">
              Dollars in, FAAB out, at the fixed $2 per {FAAB}1 — <Rule id="2020-I-6">2020-I-6</Rule>.
              {' '}Unlimited, and allowed in the offseason.
            </p>
          </>
        )}

        {kind === 'waivers' && (
          <>
            <div className="fbfields">
              <label className="fbfield grow">
                <input value={player} placeholder="Player claimed"
                  onChange={(e) => setPlayer(e.target.value)} /></label>
              <label className="fbfield num"><span className="pre">&minus;{FAAB}</span>
                <input value={bid} inputMode="numeric"
                  onChange={(e) => setBid(e.target.value)} /></label>
              <label className="fbfield">
                <span className="vh">Week</span>
                <select value={week} onChange={(e) => setWeek(e.target.value)}>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select></label>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>Debit {faab(Number(bid) || 0)}</button>
            </div>
            <p className="fbhelp">
              The winning bid only — losing bids cost nothing. Yahoo runs the claim, so this is a
              catch-up entry when the import missed one.
            </p>
          </>
        )}

        {kind === 'fine' && (
          <>
            <div className="fbfields">
              <label className="fbfield grow">
                <span className="vh">Offence</span>
                <select value={offence} onChange={(e) => setOffence(e.target.value)}>
                  {OFFENCES.map((o) => <option key={o.id} value={o.id}>{o.what}</option>)}
                </select></label>
              {/* READ-ONLY, from the rule. A commissioner choosing the number is how a rule
                  quietly becomes a habit. */}
              <span className="fbfield num derived">
                <span className="pre">&minus;{FAAB}</span><b>{fine.faab}</b>
              </span>
              <label className="fbfield">
                <input value={note} placeholder="Player fined"
                  onChange={(e) => setNote(e.target.value)} /></label>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>Charge {faab(fine.faab)}</button>
            </div>
            <p className="fbhelp">
              The amount comes from the rule, not from you: {faab(5)} a post-draft drop,
              {' '}{faab(50)} refusing a rename. A negative balance carries forward rather than
              resetting. <Rule id={fine.rule}>{fine.rule}</Rule>
            </p>
          </>
        )}

        {kind === 'keeper' && (
          <>
            <div className="fbfields">
              {/*
                READ-ONLY, FROM HIS SLATE. Same rule as the fine: the number is the rule's, not the
                commissioner's. What the field shows depends on where he stands, and the sentence
                beside it says which case this is rather than leaving a disabled button to explain.
              */}
              <span className="fbfield num derived" aria-live="polite">
                <span className="pre">{keeper?.row && keeper.row.delta < 0 ? '+' : '\u2212'}{FAAB}</span>
                <b>{keeper?.row ? Math.abs(keeper.row.delta) : keeper?.tax ?? 0}</b>
              </span>
              <span className="fbjoin fbfact">
                {who === '' ? 'Pick a GM'
                  : decls === null ? 'Reading slates\u2026'
                    : !keeper?.submitted ? 'No submitted slate, so nothing is owed'
                      : keeper.tax === 0 ? 'Inside his free keepers \u2014 no tax'
                        : !keeper.row ? `Already charged ${faab(keeper.tax)}`
                          : keeper.row.already === 0
                            ? `Owes ${faab(keeper.row.owed)} on his slate`
                            : `Slate now prices at ${faab(keeper.row.owed)}; ledger has ${faab(keeper.row.already)}`}
              </span>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>
                {keeper?.row
                  ? (keeper.row.delta < 0 ? `Credit ${faab(-keeper.row.delta)}` : `Charge ${faab(keeper.row.delta)}`)
                  : 'Charge'}
              </button>
            </div>
            <p className="fbhelp">
              Assessed from his declaration, not typed: 4 free, then the cumulative table
              {' '}<Rule id="2023-keeper-expansion-eight">2023-keeper-expansion-eight</Rule>. A
              franchise tag counts as three slots. League Overview charges all ten at once when
              declarations lock; this is the same row, for one man.
            </p>
          </>
        )}

        {kind === 'trade' && (
          <>
            <div className="fbfields">
              <span className="fbjoin">sends</span>
              <label className="fbfield num"><span className="pre">{FAAB}</span>
                <input value={scrip} inputMode="numeric" aria-label="How much FAAB"
                  onChange={(e) => setScrip(e.target.value)} /></label>
              <span className="fbjoin">to</span>
              <label className="fbpick">
                <span className="vh">Which GM receives it</span>
                <select value={toGm} onChange={(e) => setToGm(e.target.value)}>
                  <option value="">Which GM</option>
                  {GMS.filter((g) => g !== who)
                    .map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
                </select>
              </label>
              <label className="fbfield grow">
                <input value={note} placeholder="What the deal was — optional"
                  onChange={(e) => setNote(e.target.value)} /></label>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>
                Move {faab(Math.abs(Number(scrip) || 0))}
              </button>
            </div>
            <p className="fbhelp">
              Two rows, equal and opposite, so the league&rsquo;s total does not move. A trade
              settled in the app posts its own FAAB — this is for the ones that never went through
              the form.
            </p>
          </>
        )}

        {kind === 'correction' && (
          <>
            <div className="fbfields">
              <div className="fbsign" role="group" aria-label="Direction">
                {(['−', '+'] as const).map((s) => (
                  <button key={s} type="button" aria-pressed={sign === s}
                    className={sign === s ? 'on' : ''} onClick={() => setSign(s)}>{s}</button>
                ))}
              </div>
              <label className="fbfield num"><span className="pre">{FAAB}</span>
                <input value={amount} inputMode="numeric"
                  onChange={(e) => setAmount(e.target.value)} /></label>
              <label className="fbfield grow">
                <input value={reason} placeholder="Reason — required, and shown in the ledger"
                  onChange={(e) => setReason(e.target.value)} /></label>
              <button type="button" className="fbgo" disabled={!ready || busy}
                onClick={() => void save()}>Correct</button>
            </div>
            <p className="fbhelp">
              The one kind with no rule behind it, so it cannot be saved without a reason. Use it to
              restate a balance, never to move money.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
